import { Clear as ClearIcon } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { GenericParser, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';
import { useListViewContext } from '../context/ListViewContext';

/**
 * Props for the generic text filter component.
 */
export interface GenericTextFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label */
  label: MessageDescriptor;
  /** Optional message descriptor for placeholder text */
  placeholder?: MessageDescriptor;

  /** Parser for reading/writing this filter value from/to URL (from FilterDefinition). Handles defaults via .withDefault(). */
  parser: GenericParser<string | null>;
  /** Whether the filter is active (addable filters). Undefined for permanent filters. */
  isActive?: boolean;
  /** Callback when filter is cleared (addable filters). */
  onRemove?: () => void;

  /** Custom styles for the text field */
  sx?: SystemStyleObject<Theme>;
}

/**
 * Generic text filter component with debounced input.
 * Writes go through ListView context (setFilterValue) so filter changes are bundled with a page reset.
 * Reads come from nuqs (useQueryState) for simplicity. Local state provides immediate UI feedback.
 */
export function GenericTextFilter({
  name,
  label,
  placeholder,
  parser,
  isActive,
  onRemove: _onRemove,
  sx,
}: GenericTextFilterProps) {
  const { $t } = useIntl();
  const { setFilterValue } = useListViewContext();

  // Read-only: get current value from URL (parent's useQueryStates manages it)
  const [urlValue] = useQueryState(name, parser);

  // Local state for immediate UI feedback while typing
  const [localValue, setLocalValue] = useState(urlValue);

  // Sync down: URL -> local state (handles back button, Reset Filters, or external navigation)
  useEffect(() => {
    setLocalValue(urlValue);
  }, [urlValue]);

  // Debounced setter: delegates to context so pageIndex is reset when the filter changes
  const debouncedSetUrl = useDebouncedCallback((value: string) => {
    setFilterValue(name, value || null);
  }, 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setLocalValue(nextValue);
    debouncedSetUrl(nextValue);
  };

  const handleClear = () => {
    setLocalValue('');
    debouncedSetUrl.cancel();
    setFilterValue(name, null);
  };

  const translatedLabel = $t(label);
  const translatedPlaceholder = placeholder ? $t(placeholder) : undefined;

  // If it's an addable filter with no value and not active, don't render (same as GenericSelectFilter)
  if (isActive === false && !urlValue) {
    return null;
  }

  return (
    <TextField
      size="small"
      label={translatedLabel}
      placeholder={translatedPlaceholder}
      value={localValue}
      onChange={handleChange}
      sx={[
        {
          width: '18ch',
        },
        sx ?? {},
      ]}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={$t({
                id: 'common.clear',
                defaultMessage: 'Clear',
              })}
              onClick={handleClear}
              edge="end"
              size="small"
              disabled={!localValue?.length}
              sx={!localValue?.length ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}
